import type { IconType } from "react-icons";
import {
  FiActivity,
  FiBarChart2,
  FiBriefcase,
  FiGlobe,
  FiLayers,
  FiShield,
  FiUsers,
} from "react-icons/fi";

export interface ServiceSection {
  title: string;
  description?: string;
  bullets?: string[];
}

export interface ServiceShowcaseGroup {
  title: string;
  items: string[];
}

export interface ServiceRecord {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  heroTitle: string;
  heroSubtitle: string;
  intro: string;
  icon: IconType;
  accentColor: string;
  imageLabel: string;
  serviceAreas: string[];
  sections: ServiceSection[];
  showcaseTitle?: string;
  showcase?: ServiceShowcaseGroup[];
  note?: string;
  ctaTitle: string;
  ctaDescription: string;
  ctaLabel: string;
  ctaHref?: string;
  ctaIsExternal?: boolean;
}

export const SERVICES_DATA: ServiceRecord[] = [
  {
    id: "financial-advisory",
    title: "مشاوره مالی و پذیرش",
    shortTitle: "مشاوره مالی",
    description:
      "ارائه خدمات تخصصی ارزش‌گذاری، پذیرش، افزایش سرمایه، تدوین گزارش‌های توجیهی و تعهد پذیره‌نویسی برای شرکت‌ها و پروژه‌ها.",
    heroTitle: "خدمات مشاوره مالی و پذیرش",
    heroSubtitle:
      "از آماده‌سازی پذیرش و ارزش‌گذاری تا افزایش سرمایه و تعهد پذیره‌نویسی، در کنار شرکت‌ها برای اجرای مسیرهای تامین مالی بازار سرمایه هستیم.",
    intro:
      "مدیریت خدمات مشاوره مالی در شرکت تأمین سرمایه امید به‌منظور ارائه خدمات تخصصی مرتبط با عرضه و پذیرش اوراق بهادار سرمایه‌ای، ارزش‌گذاری، تدوین طرح‌های کسب‌وکار و گزارش‌های توجیهی و همچنین ارائه خدمات تعهد پذیره‌نویسی و مشاوره ادغام و تملیک تشکیل شده است.",
    icon: FiBriefcase,
    accentColor: "brand",
    imageLabel: "عرضه و پذیرش",
    serviceAreas: [
      "ارزش‌گذاری شرکت‌ها و پروژه‌ها",
      "مشاوره افزایش سرمایه",
      "پذیرش و عرضه سهام در بورس و فرابورس",
      "تدوین گزارش توجیهی مالی و اقتصادی (FS)",
      "تعهد پذیره‌نویسی و خرید سهام / حق‌تقدم‌های استفاده‌نشده",
      "تعهد خرید سهام فروش‌نرفته در روز عرضه اولیه",
    ],
    sections: [
      {
        title: "ارزش‌گذاری شرکت‌ها و پروژه‌ها",
        description:
          "ارزش‌گذاری با هدف تصمیم‌سازی برای عرضه اولیه، معاملات عمده و بلوکی و همچنین پروژه‌های ادغام و تملیک انجام می‌شود.",
      },
      {
        title: "مشاوره افزایش سرمایه",
        description:
          "خدمات افزایش سرمایه شامل طراحی مسیر تامین مالی، تهیه مستندات، هماهنگی‌های مقرراتی و پیگیری فرآیند تا اجرا است.",
      },
      {
        title: "پذیرش و عرضه سهام",
        bullets: [
          "سنجش آمادگی پذیرش و ارائه نقشه راه انطباق",
          "تهیه و تکمیل مستندات و پیگیری پذیرش به نمایندگی از متقاضی",
          "تهیه امیدنامه، طرح کسب‌وکار و پرسشنامه پذیرش",
          "پیگیری شروط هیئت پذیرش و انجام اقدامات اصلاحی",
          "همراهی در فرآیند درج، عرضه و جلسات قیمت‌گذاری پایه",
          "نظارت بر رعایت الزامات مقرراتی در فرآیند پذیرش و عرضه",
        ],
      },
      {
        title: "گزارش توجیهی مالی و اقتصادی",
        description:
          "تهیه گزارش‌های توجیهی مالی و اقتصادی برای ارزیابی امکان‌پذیری و تصمیم‌سازی سرمایه‌گذاری در پروژه‌های صنایع مختلف.",
      },
      {
        title: "تعهد پذیره‌نویسی و تعهد خرید",
        description:
          "تأمین سرمایه امید مطابق قرارداد و ضوابط سازمان بورس، در صورت عدم تکمیل فرآیند افزایش سرمایه یا عرضه اولیه، تعهدات خرید مربوط را طبق مقررات اجرا می‌کند.",
      },
    ],
    showcaseTitle: "نمونه پروژه‌ها و سوابق اجرایی",
    showcase: [
      {
        title: "ارزش‌گذاری با هدف عرضه اولیه",
        items: [
          "سیمرغ",
          "سپ",
          "کگهر",
          "های‌وب",
          "بجهرم",
          "صبا فولاد خلیج فارس",
          "فولاد سیرجان ایرانیان",
          "بانک مسکن",
        ],
      },
      {
        title: "ارزش‌گذاری با هدف معامله بلوکی",
        items: [
          "وامید",
          "میدکو",
          "فولاد زرند ایرانیان",
          "فولاد بوتیا ایرانیان",
          "وسپه",
          "سرمایه‌گذاری ری",
          "وهتر",
        ],
      },
      {
        title: "ادغام و تملیک",
        items: [
          "بانک انصار / ثامن / مهر اقتصاد",
          "پتروشیمی کوروش",
          "قشهد",
          "ختور",
          "بنو",
        ],
      },
      {
        title: "نمونه افزایش سرمایه",
        items: [
          "چادرملو",
          "وسپه",
          "پگاه اصفهان",
          "نفت بهران",
          "کویر تایر",
          "پارس دارو",
          "پست بانک ایران",
          "آهن و فولاد ارفع",
        ],
      },
      {
        title: "نمونه شرکت‌های پذیرش‌شده",
        items: [
          "تأمین سرمایه امید (بازار دوم بورس تهران)",
          "توسعه مولد نیروگاهی جهرم (بازار دوم بورس تهران)",
        ],
      },
      {
        title: "نمونه پروژه‌های FS",
        items: [
          "گندله‌سازی",
          "فولادسازی",
          "نیروگاه",
          "تامین آب از خلیج فارس",
          "پتروشیمی کوروش",
          "نیروگاه خورشیدی",
          "تایرسازی",
          "تولید کاغذ سنگ",
        ],
      },
    ],
    note: "طبقه‌بندی نهایی برخی سوابق بورسی و غیربورسی می‌تواند در مرحله تکمیل محتوا توسط تیم کسب‌وکار نهایی شود.",
    ctaTitle: "برای پذیرش، ارزش‌گذاری یا افزایش سرمایه آماده‌ایم",
    ctaDescription:
      "اگر برای عرضه، پذیرش یا تدوین مدارک تامین مالی به همراهی تخصصی نیاز دارید، مسیر اجرایی را با تیم امید شروع کنید.",
    ctaLabel: "درخواست مشاوره",
  },
  {
    id: "domestic-financing",
    title: "تامین مالی داخلی",
    shortTitle: "تامین مالی داخلی",
    description:
      "طراحی و اجرای راهکارهای مبتنی بر بازار سرمایه برای بنگاه‌ها و پروژه‌ها با تکیه بر ابزارهای مالی اسلامی و ساختارهای متناسب با هر نیاز.",
    heroTitle: "راهکارهای تامین مالی داخلی",
    heroSubtitle:
      "برای شرکت‌ها و پروژه‌هایی که به دنبال مسیرهای متنوع تامین مالی در بازار سرمایه ایران هستند، ساختار مناسب را طراحی و پیگیری می‌کنیم.",
    intro:
      "تامین مالی داخلی شامل طراحی و اجرای راهکارهای مبتنی بر بازار سرمایه برای بنگاه‌ها و پروژه‌ها در چارچوب قوانین و ابزارهای مالی اسلامی است. در ایران، به‌جای اوراق قرضه متعارف، ابزارهایی مانند انواع صکوک و صندوق‌های پروژه‌محور به‌کار گرفته می‌شود.",
    icon: FiLayers,
    accentColor: "secondary",
    imageLabel: "راهکارهای ریالی",
    serviceAreas: [
      "اوراق مشارکت و صکوک اجاره",
      "مرابحه، سلف موازی استاندارد و منفعت",
      "استصناع، جعاله، رهنی و خرید دین",
      "صندوق زمین و ساختمان و صندوق پروژه",
      "صندوق جسورانه و صندوق خصوصی",
      "افزایش سرمایه و تاسیس شرکت سهامی عام",
    ],
    sections: [
      {
        title: "روش‌های مبتنی بر بدهی",
        bullets: [
          "اوراق مشارکت",
          "صکوک اجاره برای تامین نقدینگی یا تامین دارایی",
          "مرابحه",
          "سلف موازی استاندارد",
          "منفعت، استصناع، جعاله و رهنی",
          "خرید دین",
        ],
      },
      {
        title: "روش‌های مبتنی بر سرمایه",
        bullets: [
          "صندوق زمین و ساختمان",
          "صندوق پروژه",
          "صندوق جسورانه",
          "صندوق خصوصی",
          "افزایش سرمایه",
          "تاسیس شرکت‌های سهامی عام",
        ],
      },
      {
        title: "رویکرد اجرایی",
        description:
          "در هر پروژه، انتخاب ابزار بر اساس ساختار نقدینگی، ماهیت دارایی، دوره بازپرداخت، ظرفیت بازار و الزامات مقرراتی انجام می‌شود تا مسیر تامین مالی به‌صورت قابل اجرا و متناسب با نیاز واقعی طراحی شود.",
      },
    ],
    showcaseTitle: "نمونه کاربردهای متداول",
    showcase: [
      {
        title: "راهکارهای بدهی‌محور",
        items: [
          "تامین نقدینگی کوتاه‌مدت و میان‌مدت",
          "تامین مالی مبتنی بر دارایی",
          "مدیریت بدهی و بازآرایی ساختار تامین مالی",
        ],
      },
      {
        title: "راهکارهای سرمایه‌محور",
        items: [
          "توسعه پروژه‌های ساختمانی و زیرساختی",
          "تامین مالی طرح‌های توسعه‌ای شرکتی",
          "جذب سرمایه برای کسب‌وکارهای رشدگرا",
        ],
      },
    ],
    ctaTitle: "مسیر مناسب تامین مالی را با هم انتخاب کنیم",
    ctaDescription:
      "برای بررسی ساختار بدهی‌محور یا سرمایه‌محور متناسب با پروژه، اطلاعات اولیه را با تیم ما به اشتراک بگذارید.",
    ctaLabel: "ثبت درخواست مشاوره",
  },
  {
    id: "international-financing",
    title: "تامین مالی بین‌المللی",
    shortTitle: "تامین مالی بین‌المللی",
    description:
      "ارائه خدمات مشاوره و طراحی راهکارهای همکاری مالی فرامرزی برای پروژه‌ها و کسب‌وکارهای واجد شرایط با رویکردی واقع‌بینانه و اجرایی.",
    heroTitle: "خدمات تامین مالی بین‌المللی",
    heroSubtitle:
      "با تکیه بر تجربه تامین مالی و سرمایه‌گذاری، برای پروژه‌های واجد شرایط راهکارهای همکاری فرامرزی و ساختارهای قابل اتکا طراحی می‌کنیم.",
    intro:
      "تأمین سرمایه امید با اتکا به تجربه تامین مالی و سرمایه‌گذاری و با رویکرد توسعه همکاری‌های فرامرزی، خدمات مشاوره و طراحی راهکارهای تامین مالی بین‌المللی را برای پروژه‌ها و کسب‌وکارهای واجد شرایط ارائه می‌دهد.",
    icon: FiGlobe,
    accentColor: "accent2",
    imageLabel: "همکاری فرامرزی",
    serviceAreas: [
      "دسترسی به منابع مالی متنوع‌تر",
      "جذب شرکای راهبردی",
      "تنوع‌بخشی به ساختار تامین مالی",
      "پشتیبانی از اجرای پروژه‌های مقیاس‌پذیر",
    ],
    sections: [
      {
        title: "چرا تامین مالی بین‌المللی؟",
        bullets: [
          "دسترسی به منابع مالی متنوع‌تر",
          "جذب شرکای راهبردی",
          "تنوع‌بخشی به ساختار تامین مالی",
          "پشتیبانی از اجرای پروژه‌های مقیاس‌پذیر",
        ],
      },
      {
        title: "نوع خدمات قابل ارائه",
        description:
          "این خدمات با تمرکز بر طراحی ساختار، ارزیابی قابلیت اجرا، شناسایی طرف‌های بالقوه و آماده‌سازی اولیه برای همکاری‌های فرامرزی ارائه می‌شود.",
      },
      {
        title: "رویکرد واقع‌بینانه",
        description:
          "در این حوزه، طراحی راهکارها باید هم‌زمان با ملاحظات مقرراتی، محدودیت‌های عملیاتی و ظرفیت واقعی پروژه انجام شود تا خروجی نهایی قابل پیگیری باشد.",
      },
    ],
    showcaseTitle: "خروجی‌های مورد انتظار",
    showcase: [
      {
        title: "محورهای طراحی",
        items: [
          "ارزیابی آمادگی پروژه برای تعاملات فرامرزی",
          "طراحی ساختار همکاری و تامین مالی",
          "آماده‌سازی بسته معرفی پروژه برای مذاکره",
        ],
      },
      {
        title: "مزیت‌های هدف‌گذاری‌شده",
        items: [
          "گسترش گزینه‌های تامین مالی",
          "کاهش تمرکز بر یک منبع تامین سرمایه",
          "تقویت امکان اجرای پروژه‌های توسعه‌ای",
        ],
      },
    ],
    note: "محدودیت‌های قانونی، تحریمی و الزامات نهادهای ناظر باید از ابتدا در طراحی ساختار و دامنه خدمات لحاظ شوند.",
    ctaTitle: "برای ارزیابی ظرفیت همکاری بین‌المللی آماده‌ایم",
    ctaDescription:
      "اگر پروژه یا کسب‌وکار شما نیازمند بررسی ظرفیت‌های تامین مالی فرامرزی است، گفت‌وگو را با یک ارزیابی اولیه آغاز کنیم.",
    ctaLabel: "شروع ارزیابی",
  },
  {
    id: "investment-and-market-making",
    title: "سرمایه‌گذاری و بازارگردانی",
    shortTitle: "بازارگردانی",
    description:
      "کمک به تنظیم عرضه و تقاضا، افزایش نقدشوندگی و حمایت از شکل‌گیری بازاری منظم و روان در چارچوب دستورالعمل‌های مصوب.",
    heroTitle: "سرمایه‌گذاری و بازارگردانی",
    heroSubtitle:
      "فعالیت بازارگردانی با هدف تقویت نقدشوندگی، مدیریت نوسان‌های غیرمنطقی و پشتیبانی از کیفیت معاملات انجام می‌شود.",
    intro:
      "فعالیت بازارگردانی در چارچوب دستورالعمل‌های مصوب و با هدف تنظیم عرضه و تقاضا، محدودسازی نوسانات غیرمنطقی و افزایش نقدشوندگی اوراق بهادار انجام می‌شود. بازارگردان با انجام سفارش‌های خرید و فروش در دامنه‌های تعیین‌شده، به حفظ بازار منظم و روان کمک می‌کند.",
    icon: FiBarChart2,
    accentColor: "orange",
    imageLabel: "بازارگردانی",
    serviceAreas: [
      "تقویت نقدشوندگی و عمق بازار",
      "کمک به تعادل عرضه و تقاضا در شرایط رکودی یا هیجانی",
      "کاهش رفتارهای قیمتی نامتعارف در چارچوب ضوابط",
    ],
    sections: [
      {
        title: "معرفی بازارگردانی",
        description:
          "بازارگردانی بخشی از زیرساخت سلامت معاملات است و نقش آن ایجاد تعادل عملیاتی، حمایت از نقدشوندگی و کاهش اختلال‌های کوتاه‌مدت در جریان عرضه و تقاضا است.",
      },
      {
        title: "نقش بازارگردان",
        bullets: [
          "تقویت نقدشوندگی و عمق بازار",
          "کمک به تعادل عرضه و تقاضا در شرایط رکودی یا هیجانی",
          "کاهش رفتارهای قیمتی نامتعارف در چارچوب ضوابط",
        ],
      },
      {
        title: "منطق اجرایی",
        description:
          "اجرای این خدمت بر پایه دستورالعمل‌های مصوب، دامنه‌های عملیاتی مشخص و پایش مستمر رفتار معاملات انجام می‌شود تا کیفیت بازار به‌شکل پایدار حفظ شود.",
      },
    ],
    showcaseTitle: "کاربردهای کلیدی",
    showcase: [
      {
        title: "اهداف اجرایی",
        items: [
          "بهبود کیفیت معاملات",
          "حمایت از نقدشوندگی اوراق",
          "پشتیبانی از ثبات نسبی بازار",
        ],
      },
      {
        title: "اثر مورد انتظار",
        items: [
          "بازار منظم‌تر",
          "کاهش نوسان‌های نامتعارف",
          "بهبود تجربه معامله‌گری سرمایه‌گذاران",
        ],
      },
    ],
    ctaTitle: "برای بررسی خدمات بازارگردانی گفت‌وگو کنیم",
    ctaDescription:
      "اگر برای اوراق یا ابزار مالی خود به ساختار بازارگردانی و چارچوب اجرایی نیاز دارید، می‌توانیم سناریوهای مناسب را بررسی کنیم.",
    ctaLabel: "درخواست جلسه تخصصی",
  },
  {
    id: "asset-management",
    title: "مدیریت دارایی",
    shortTitle: "مدیریت دارایی",
    description:
      "تشکیل و مدیریت حرفه‌ای سبدهای متنوع دارایی با توجه به سطح ریسک مورد انتظار سرمایه‌گذاران و استفاده از ظرفیت صندوق‌ها و سبدهای اختصاصی.",
    heroTitle: "خدمات مدیریت دارایی",
    heroSubtitle:
      "برای سرمایه‌گذارانی که به دنبال مدیریت حرفه‌ای، تنوع دارایی و انتخاب متناسب با سطح ریسک هستند، راهکارهای مدیریت دارایی ارائه می‌کنیم.",
    intro:
      "مدیریت دارایی با هدف تشکیل و مدیریت حرفه‌ای سبدی متنوع از دارایی‌ها و ایجاد بازدهی متناسب با سطح ریسک مورد انتظار سرمایه‌گذاران انجام می‌شود. صندوق‌های سرمایه‌گذاری و سبدهای اختصاصی از ابزارهای اصلی مدیریت دارایی هستند و فعالیت آن‌ها تحت نظارت سازمان بورس و اوراق بهادار صورت می‌گیرد.",
    icon: FiUsers,
    accentColor: "teal",
    imageLabel: "مدیریت سرمایه",
    serviceAreas: [
      "صندوق‌های سرمایه‌گذاری",
      "سبدهای اختصاصی",
      "تنوع‌بخشی دارایی‌ها",
      "مدیریت تخصصی متناسب با ریسک",
    ],
    sections: [
      {
        title: "معرفی مدیریت دارایی",
        description:
          "هدف این خدمت، ترکیب مناسب دارایی‌ها، کنترل ریسک و ایجاد بازدهی متناسب با ترجیحات سرمایه‌گذار در افق‌های زمانی مختلف است.",
      },
      {
        title: "صندوق سرمایه‌گذاری چیست؟",
        description:
          "صندوق سرمایه‌گذاری، تجمیع سرمایه سرمایه‌گذاران و سرمایه‌گذاری آن در مجموعه‌ای از دارایی‌ها مانند اوراق با درآمد ثابت و سهام توسط تیم حرفه‌ای است؛ تنوع دارایی‌ها به کاهش ریسک و مدیریت تخصصی به تصمیم‌گیری بهتر کمک می‌کند.",
      },
      {
        title: "راهکارهای متناسب با سرمایه‌گذار",
        bullets: [
          "انتخاب ابزار بر اساس سطح ریسک مورد انتظار",
          "بهره‌گیری از مدیریت حرفه‌ای و تصمیم‌گیری تخصصی",
          "استفاده از تنوع سبد برای کنترل ریسک",
        ],
      },
    ],
    showcaseTitle: "خروجی‌های کلیدی این خدمت",
    showcase: [
      {
        title: "برای سرمایه‌گذاران حقیقی",
        items: [
          "دسترسی ساده‌تر به مدیریت حرفه‌ای",
          "انتخاب میان صندوق‌های متنوع",
          "تنوع‌بخشی و کنترل بهتر ریسک",
        ],
      },
      {
        title: "برای سرمایه‌گذاران نهادی",
        items: [
          "طراحی سبد متناسب با الزامات سرمایه‌گذاری",
          "رصد مداوم عملکرد و ریسک",
          "هم‌راستاسازی ترکیب دارایی با اهداف مالی",
        ],
      },
    ],
    ctaTitle: "برای طراحی مسیر مدیریت دارایی آماده‌ایم",
    ctaDescription:
      "اگر به دنبال ترکیب مناسب صندوق‌ها یا طراحی راهکار اختصاصی سرمایه‌گذاری هستید، گفت‌وگو را از همین‌جا شروع کنید.",
    ctaLabel: "دریافت مشاوره سرمایه‌گذاری",
  },
  {
    id: "risk-management",
    title: "مدیریت ریسک و مهندسی مالی",
    shortTitle: "مدیریت ریسک",
    description:
      "شناسایی، ارزیابی و کنترل ریسک‌های اثرگذار بر عملکرد شرکت و طراحی راهکارهای پوشش ریسک و ابزارهای نوین مالی.",
    heroTitle: "مدیریت ریسک و مهندسی مالی",
    heroSubtitle:
      "از استقرار مدیریت ریسک سازمانی تا تحقیق، توسعه و آموزش، رویکردی ساختاریافته برای کنترل ریسک و توسعه ابزارهای نوین ارائه می‌کنیم.",
    intro:
      "مدیریت ریسک و مهندسی مالی با هدف شناسایی، ارزیابی و کنترل ریسک‌های اثرگذار بر عملکرد شرکت و همچنین ارائه راهکارهای پوشش ریسک و طراحی ابزارهای مالی نوین فعالیت می‌کند.",
    icon: FiShield,
    accentColor: "purple",
    imageLabel: "ریسک و نوآوری",
    serviceAreas: [
      "مدیریت ریسک",
      "طراحی ابزارهای نوین مالی",
      "تحقیق و توسعه",
      "مطالعات و رصد بازار",
      "آموزش و فرهنگ‌سازی در بازار",
    ],
    sections: [
      {
        title: "حوزه‌های فعالیت",
        bullets: [
          "مدیریت ریسک",
          "طراحی ابزارهای نوین مالی",
          "تحقیق و توسعه",
          "مطالعات و رصد بازار",
          "آموزش و فرهنگ‌سازی در بازار",
        ],
      },
      {
        title: "رویکرد اجرا",
        bullets: [
          "بعد درون‌سازمانی: استقرار و تقویت مدیریت ریسک سازمانی برای ارتقای کارایی و کنترل ریسک‌های شرکت",
          "بعد خدماتی: ارائه مشاوره و پیاده‌سازی مدیریت ریسک سازمانی برای سازمان‌ها و شرکت‌های متقاضی در چارچوب آیین‌نامه‌ها و الزامات نهادهای ناظر",
        ],
      },
      {
        title: "اقدامات آموزشی",
        description:
          "در راستای ارتقای سواد مالی و فرهنگ سرمایه‌گذاری، آکادمی مالی امید و دیکشنری تخصصی مالی امید به‌عنوان محصولات آموزشی توسعه یافته‌اند.",
      },
    ],
    showcaseTitle: "محورهای ارزش‌آفرین",
    showcase: [
      {
        title: "برای سازمان",
        items: [
          "تقویت کنترل ریسک‌های کلیدی",
          "ارتقای کارایی تصمیم‌گیری",
          "بهبود آمادگی برای الزامات نظارتی",
        ],
      },
      {
        title: "برای بازار و مشتریان",
        items: [
          "توسعه دانش کاربردی مالی",
          "گسترش ابزارها و راهکارهای نوآورانه",
          "تقویت فرهنگ سرمایه‌گذاری آگاهانه",
        ],
      },
    ],
    ctaTitle: "برای طراحی چارچوب مدیریت ریسک گفت‌وگو کنیم",
    ctaDescription:
      "اگر نیاز به استقرار مدیریت ریسک، مطالعات بازار یا طراحی ابزارهای نوین دارید، می‌توانیم دامنه کار را با هم تعریف کنیم.",
    ctaLabel: "درخواست بررسی تخصصی",
  },
  {
    id: "omidcrowd",
    title: "تامین مالی جمعی (امیدکراد)",
    shortTitle: "امیدکراد",
    description:
      "سکوی رسمی تامین مالی جمعی تأمین سرمایه امید برای جذب سرمایه طرح‌های اقتصادی و نوآورانه از طریق مشارکت آنلاین و ضابطه‌مند سرمایه‌گذاران.",
    heroTitle: "تامین مالی جمعی با امیدکراد",
    heroSubtitle:
      "امیدکراد پل ارتباطی شفاف میان متقاضیان سرمایه و سرمایه‌گذاران است و فرآیند جذب منابع را در بستری رسمی و ضابطه‌مند پیش می‌برد.",
    intro:
      "تامین مالی جمعی روشی نوین برای جذب منابع مالی پروژه‌ها و کسب‌وکارها از طریق مشارکت سرمایه‌گذاران با مبالغ خرد در بستر آنلاین و ضابطه‌مند است. این فرآیند در چارچوب دستورالعمل‌های مصوب و با نظارت نهادهای ذی‌صلاح انجام می‌شود.",
    icon: FiActivity,
    accentColor: "green",
    imageLabel: "امیدکراد",
    serviceAreas: [
      "جذب سرمایه برای طرح‌های اقتصادی و نوآورانه",
      "ایجاد ارتباط مستقیم و شفاف میان متقاضی و تامین‌کننده سرمایه",
      "اجرای فرآیند در بستر آنلاین و ضابطه‌مند",
      "فعالیت تحت نظارت نهادهای ذی‌صلاح",
    ],
    sections: [
      {
        title: "سکوی رسمی امیدکراد",
        description:
          "امیدکراد سکوی رسمی تامین مالی جمعی شرکت تأمین سرمایه امید است که با هدف تسهیل جذب سرمایه برای طرح‌های اقتصادی و نوآورانه راه‌اندازی شده و ارتباط مستقیم و شفاف میان متقاضی و تامین‌کننده سرمایه را برقرار می‌کند.",
      },
      {
        title: "مجوز فعالیت",
        description:
          "مجوز فعالیت فرابورس ایران با شماره 14658840 مورخ 18/12/1403 برای این سکو صادر شده است.",
      },
      {
        title: "موارد غیرمجاز برای تامین منابع از طریق سکوها",
        bullets: [
          "دستگاه‌های اجرایی موضوع ماده ۵ قانون مدیریت خدمات کشوری",
          "بانک‌ها و موسسات مالی و اعتباری",
          "نهادهای مالی دارای مجوز از سازمان",
          "شرکت‌های پذیرفته‌شده در بورس و بازار اول و دوم فرابورس",
          "شرکت‌های درج‌شده در بازار پایه فرابورس",
        ],
      },
    ],
    showcaseTitle: "مزیت‌های بستر امیدکراد",
    showcase: [
      {
        title: "برای متقاضیان سرمایه",
        items: [
          "دسترسی به جامعه وسیع‌تری از سرمایه‌گذاران",
          "فرآیند شفاف و آنلاین معرفی طرح",
          "پیگیری ساختاریافته جذب منابع",
        ],
      },
      {
        title: "برای سرمایه‌گذاران",
        items: [
          "امکان مشارکت با مبالغ خرد",
          "دریافت اطلاعات شفاف‌تر درباره طرح‌ها",
          "سرمایه‌گذاری در بستر رسمی و ضابطه‌مند",
        ],
      },
    ],
    ctaTitle: "برای استفاده از ظرفیت امیدکراد با ما در تماس باشید",
    ctaDescription:
      "اگر قصد تامین سرمایه برای یک طرح واجد شرایط را دارید یا می‌خواهید درباره فرآیندهای امیدکراد بیشتر بدانید، مسیر گفت‌وگو از همین‌جا آغاز می‌شود.",
    ctaLabel: "صفحه اصلی امید کراد",
    ctaHref: "https://omidcf.ir/",
    ctaIsExternal: true,
  },
];

export const getServiceById = (serviceId?: string) =>
  SERVICES_DATA.find((service) => service.id === serviceId);
